import { z } from 'zod';
import {
  orgUnitSchema,
  type CreateOrgUnitInput,
  type OrgUnitDto,
  type UpdateOrgUnitInput,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Backend javoblarining ish vaqtidagi sxemasi. Manba:
 *   `org/infrastructure/http/org-unit.controller.ts`
 *   `org/application/org-unit.service.ts` (`OrgUnitNode = OrgUnitRow + children`)
 *
 * `GET /org-units/tree` **yassi emas**, tayyor daraxt qaytaradi — otasi
 * ro'yxatda bo'lmagan tugun ildiz bo'lib qoladi.
 */
export interface OrgUnitNode extends OrgUnitDto {
  children: OrgUnitNode[];
}

export const orgUnitNodeSchema: z.ZodType<OrgUnitNode> = orgUnitSchema.extend({
  children: z.lazy(() => z.array(orgUnitNodeSchema)),
});

export const orgUnitPageSchema = z.object({
  items: z.array(orgUnitSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export interface OrgUnitQuery {
  mtuId?: string;
  type?: string;
  search?: string;
  limit?: number;
  page?: number;
}

export interface OrgApi {
  listUnits: (query?: OrgUnitQuery) => Promise<z.infer<typeof orgUnitPageSchema>>;
  /**
   * BARCHA korxonalar — sahifalab yig'ib.
   *
   * Bitta so'rovda 200 tadan ko'pini olib bo'lmaydi (`paginationQuerySchema`
   * cheklovi) va bazada 500 dan ortiq korxona bor. Katta `limit` yozib
   * qo'yish yechim emas — u 400 bilan qaytadi va tanlagich JIMGINA bo'sh
   * qoladi: aynan shunday bo'lgan edi.
   *
   * Tanlagich uchun kerak, ro'yxat uchun emas: bu yerda foydalanuvchi
   * sahifalamaydi, u nomni terib qidiradi.
   */
  listAllUnits: (query?: Omit<OrgUnitQuery, 'page' | 'limit'>) => Promise<OrgUnitDto[]>;
  tree: (mtuId: string) => Promise<OrgUnitNode[]>;
  getUnit: (id: string) => Promise<OrgUnitDto>;
  createUnit: (input: CreateOrgUnitInput) => Promise<OrgUnitDto>;
  updateUnit: (id: string, input: UpdateOrgUnitInput) => Promise<OrgUnitDto>;
}

/** `paginationQuerySchema` ning ustki chegarasi — undan kattasi 400 beradi. */
const PAGE_SIZE = 200;

const queryString = (query: OrgUnitQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 200),
  });
  if (query.mtuId !== undefined) params.set('mtuId', query.mtuId);
  if (query.type !== undefined) params.set('type', query.type);
  if (query.search !== undefined && query.search !== '') params.set('search', query.search);
  return params.toString();
};

export const createOrgApi = (client: ApiClient): OrgApi => ({
  listUnits: (query = {}) =>
    client.apiFetch(`/org-units?${queryString(query)}`, { schema: orgUnitPageSchema }),

  listAllUnits: async (query = {}) => {
    /*
     * `Map` — TAKRORLANISHGA QARSHI, `[]` emas.
     *
     * Ro'yxat sahifalab yig'iladi va serverdagi tartib buzilsa bitta korxona
     * ikki sahifada uchrab qolishi mumkin. Tanlagichda bu bir xil nomli
     * ikki qator bo'lib chiqadi va foydalanuvchi qaysi birini bosishni
     * bilmaydi. Server tomonida tartib barqarorlashtirilgan
     * (`org-unit.repository.ts`), bu esa mijozdagi ikkinchi to'siq: bitta
     * `id` — bitta qator, har qanday holatda ham.
     */
    const byId = new Map<string, OrgUnitDto>();
    /* Yuqori chegara — cheksiz aylanishning oldini oladi: server kutilmagan
       javob qaytarsa ham sahifa qotib qolmaydi. 25 × 200 = 5000 korxona. */
    for (let page = 1; page <= 25; page += 1) {
      const chunk = await client.apiFetch(`/org-units?${queryString({ ...query, page, limit: PAGE_SIZE })}`, {
        schema: orgUnitPageSchema,
      });
      for (const unit of chunk.items) byId.set(unit.id, unit);
      // Sanoq YUKLANGAN yozuvlar bo'yicha, noyoblari bo'yicha emas: takror
      // kelgan sahifa ham o'qilgan hisoblanadi, aks holda tsikl 25 martagacha
      // bir xil sahifani qayta so'rab yurardi.
      if ((page - 1) * PAGE_SIZE + chunk.items.length >= chunk.total || chunk.items.length === 0) {
        break;
      }
    }
    return [...byId.values()];
  },

  tree: (mtuId) =>
    client.apiFetch(`/org-units/tree?mtuId=${encodeURIComponent(mtuId)}`, {
      schema: z.array(orgUnitNodeSchema),
    }),

  getUnit: (id) => client.apiFetch(`/org-units/${id}`, { schema: orgUnitSchema }),

  createUnit: (input) =>
    client.apiFetch('/org-units', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: orgUnitSchema,
    }),

  updateUnit: (id, input) =>
    client.apiFetch(`/org-units/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: orgUnitSchema,
    }),
});

/** Ilovaning yagona klienti ustidagi tayyor ko'rinish. */
export const orgApi = createOrgApi(apiClient);
